import React, { useEffect, useState } from "react";
import { AgGridReact, AgGridColumn } from "ag-grid-react";

import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import LogCapture from "../../utils/LogManagement/LogHelper";
import { handleReadingFile } from "../../utils/FileHandler";
import { Trans, useTranslation } from "react-i18next";
import BaseModal from "../Modals/BaseModal";
import { ACTIONS, ENTITIES } from "../../utils/LogManagement/LogConstants";

const ScansReferenceModal = ({
    modalButton,
    modalButtonSetter,
    data,
    setDataReferenceFileName,
    setReferenceProgressBarValue,
    setExternalDataReference,
    predictionHeaders,
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [rowCount, setRowCount] = useState(0);
    const [filename, setFilename] = useState(null);
    const [, setReferenceFileData] = useState({
        headers: [],
        rows: [],
    });
    const [referenceHeaders, setReferenceHeaders] = useState([]);
    const [generatedData, setGeneratedData] = useState(null);

    const [gridApi, setGridApi] = useState(null);
    const [, setGridColumnApi] = useState(null);

    const { t } = useTranslation();

    useEffect(() => {
        if (data) {
            const sampleNames = Object.values(data.scanDetails).map((scan) => {
                return { sampleName: scan.name };
            });
            const headers = ["sampleName", ...(predictionHeaders || [])];
            const rows = sampleNames.map((row) => {
                let newRow = { ...row };
                predictionHeaders.reduce((accumulated, prediction) => {
                    accumulated[prediction] = "---";
                    return accumulated;
                }, newRow);

                return newRow;
            });

            setGeneratedData({ rows, headers });
            setReferenceFileData({ rows, headers });
            setReferenceHeaders(headers);
            setRowCount(sampleNames.length);
        }
    }, [data, predictionHeaders]);

    useEffect(() => {
        if (modalButton) {
            openModal();
            modalButtonSetter(false);
        }
    }, [modalButton]);

    const referenceRowIterations = (modifiedData, row) => {
        const rows = generatedData.rows;
        let isSampleFound = false;
        let referenceRow = {};
        for (let index = 0; index < rows.length; index++) {
            referenceRow = rows[index];

            if (index === 0) {
                modifiedData.headers = Object.keys(referenceRow).splice(1);
            }
            const sampleName = referenceRow["SampleName"];
            const rowSampleName = row[0].toLowerCase();

            if (!sampleName) {
                return false;
            }

            if (rowSampleName.includes(sampleName.toLowerCase())) {
                isSampleFound = true;
                modifiedData.rows.push(Object.values(referenceRow).splice(1));
                break;
            }
        }

        if (!isSampleFound) {
            modifiedData.rows.push(
                Object.values(referenceRow)
                    .splice(1)
                    .map(() => "---")
            );
        }
        return true;
    };

    const modifyData = () => {
        const modifiedData = { headers: [], rows: [] };
        let isDataValid = true;
        if (data && data.rows) {
            data.rows.forEach((row) => {
                isDataValid = referenceRowIterations(modifiedData, row);
            });
            if (!isDataValid) {
                ErrorAlert(t("fileFormatSampleName"));
                return isDataValid;
            }
        }

        setExternalDataReference(modifiedData);
        return isDataValid;
    };

    const openModal = () => {
        setIsVisible(true);
    };

    const handleSubmit = () => {
        const isDataValid = modifyData();

        if (isDataValid) {
            setDataReferenceFileName(filename);
            SuccessAlert(t("referenceImported"));
        }
        setIsVisible(false);
        setIsSubmitting(false);
    };

    const handleFileClick = (id) => {
        const element = document.getElementById(id);
        element.click();
        element.value = "";
    };

    const handleFileRead = (event) => {
        handleReadingFile(
            event,
            () => {
                ErrorAlert(t("readingFileError"));
                LogCapture.error("Unable to read reference file for scans");
            },
            (key, retrievedFilename, result) => {
                generateData(result.rows);
                setReferenceFileData(result);
                setFilename(retrievedFilename);
                setReferenceProgressBarValue(100);
                LogCapture.info(
                    `User imported reference file for scans, filename: ${retrievedFilename}`,
                    ENTITIES.scans,
                    ACTIONS.scans.importReferenceFile
                );
            },
            "reference"
        );
    };

    const generateData = (rows) => {
        const newData = [];
        const newHeaders = [...referenceHeaders];
        rows.forEach((element) => {
            const r = {};
            newHeaders.forEach((header, headerIndex) => {
                if (headerIndex === 0) r["SampleName"] = element[headerIndex];
                else r[header] = element[headerIndex];
            });
            newData.push(r);
        });
        newHeaders.shift();
        setRowCount(0);
        setGeneratedData({
            headers: ["SampleName", ...newHeaders],
            rows: newData,
        });
        setRowCount(newData.length);
        gridApi.setRowData(newData);
    };

    const exportFile = () => {
        LogCapture.info(
            "Reference file for scans is exported successfully",
            ENTITIES.scans,
            ACTIONS.scans.exportReferenceFile
        );
        gridApi.exportDataAsCsv();
        SuccessAlert(t("fileExported"));
    };

    const onGridReady = (params) => {
        setGridApi(params.api);
        setGridColumnApi(params.columnApi);
        params.api.setRowData(generatedData.rows);
    };

    const buttonFields = [
        {
            name: "import",
            buttonStyling: "default-orange-outline-btn flex items-center",
            handler: handleFileClick,
            isFileUpload: true,
            icon: "fas fa-file-upload",
        },
        {
            name: "export",
            buttonStyling: "default-orange-outline-btn ml-2 flex items-center",
            handler: exportFile,
            isFileUpload: false,
            icon: "fas fa-file-download",
        },
    ];

    const renderButtons = () => {
        return (
            buttonFields &&
            buttonFields.map((btn, index) => (
                <button
                    key={index}
                    className={btn.buttonStyling}
                    onClick={() =>
                        btn.isFileUpload
                            ? btn.handler("openFile-2")
                            : btn.handler()
                    }
                >
                    <i className={btn.icon} />
                    <span className="ml-2">{t(btn.name)}</span>
                    {btn.isFileUpload && (
                        <input
                            id="openFile-2"
                            type="file"
                            name="data"
                            accept=".csv"
                            className="hidden"
                            onChange={handleFileRead}
                        />
                    )}
                </button>
            ))
        );
    };

    const renderGrid = () => {
        return (
            rowCount > 0 && (
                <div
                    style={{
                        height: 300,
                    }}
                    className="ag-theme-alpine text-center"
                >
                    <AgGridReact
                        overlayLoadingTemplate="<span className='ag-overlay-loading-center'>Please wait while your rows are loading</span>"
                        rowSelection="single"
                        onGridReady={onGridReady}
                        rowData={generatedData.rows}
                    >
                        {generatedData.headers.map((header, index) => {
                            return (
                                <AgGridColumn
                                    key={index}
                                    field={header}
                                    width={getWidth(index)}
                                    cellClass="text-gray-500"
                                    pinned={!index ? true : false}
                                />
                            );
                        })}
                    </AgGridReact>
                </div>
            )
        );
    };

    const getWidth = (index) => {
        if (!index) {
            return 225;
        } else if (generatedData.headers.length <= 2) {
            return 600;
        } else {
            return 150;
        }
    };

    return (
        <>
            <BaseModal
                isVisible={isVisible}
                setIsVisible={setIsVisible}
                isSubmitting={isSubmitting}
                title="Import Reference"
                titleIcon="fas fa-upload"
                action={{ name: "Done", submitAction: handleSubmit }}
            >
                <div className="main-content-header flex items-start justify-between">
                    <p className="mr-4">
                        <Trans i18nKey="scansImportMessage">
                            You can export the sample names of your Measurements
                            to help you get their references, then import them.
                        </Trans>
                    </p>
                    <div className="flex items-center">{renderButtons()}</div>
                </div>

                <div className="shadow sm:rounded-lg">{renderGrid()}</div>
            </BaseModal>
        </>
    );
};

export default ScansReferenceModal;
